import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "./ui/button";
import Logo from "./Logo";
import { 
  Gift, 
  Shield, 
  Settings, 
  LogOut,
  Loader2
} from "lucide-react";

/**
 * Shared authenticated header component.
 * Shows site-wide navigation for logged-in users including:
 * - Affiliate Dashboard button (if user is an affiliate)
 * - Admin button (if user is an admin)
 * - Settings and Logout
 */
const AuthHeader = ({ 
  showDashboardLink = false,
  adminBadgeCount = 0,
  payoutRequestsCount = 0 
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check affiliate and admin status on mount
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check affiliate status
        const affiliateRes = await api.get("/affiliates/me");
        setIsAffiliate(affiliateRes.data?.is_affiliate === true);
      } catch (err) {
        setIsAffiliate(false);
      }

      try {
        // Check admin status
        const meRes = await api.get("/auth/me");
        setIsAdmin(meRes.data?.is_admin === true);
      } catch (err) {
        setIsAdmin(false);
      }

      setLoading(false);
    };

    checkUserStatus();
  }, [user]);

  if (!user) return null;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Logo showText linkTo="/" />
        
        <div className="flex items-center gap-4">
          <span className="text-text-muted text-sm hidden md:block">{user?.email}</span>
          
          {/* Dashboard Link - optional, shown when not on dashboard */}
          {showDashboardLink && (
            <Link to="/dashboard">
              <Button variant="ghost" className="text-text-muted hover:text-white text-sm" data-testid="dashboard-btn">
                Dashboard
              </Button>
            </Link>
          )}
          
          {/* Affiliate Dashboard Link - visible to affiliates */}
          {!loading && isAffiliate && (
            <Link to="/affiliates/dashboard">
              <Button variant="ghost" className="text-text-muted hover:text-white text-sm" data-testid="affiliate-dashboard-btn">
                <Gift className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">Affiliate Dashboard</span>
                <span className="sm:hidden">Affiliate</span>
              </Button>
            </Link>
          )}
          
          {/* Admin Link - only visible to admin users */}
          {!loading && isAdmin && (
            <Link to={payoutRequestsCount > 0 ? "/admin/payouts" : "/admin"}>
              <Button variant="ghost" className="text-text-muted hover:text-white text-sm relative" data-testid="admin-btn">
                <Shield className="w-4 h-4 mr-1.5" />
                Admin
                {adminBadgeCount > 0 && (
                  <span className={`absolute -top-1 -right-1 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                    payoutRequestsCount > 0 ? "bg-green-500 text-white" : "bg-[#D4F34A] text-black"
                  }`}>
                    {adminBadgeCount > 9 ? "9+" : adminBadgeCount}
                  </span>
                )}
              </Button>
            </Link>
          )}
          
          <Link to="/settings">
            <Button variant="ghost" size="icon" className="text-text-muted hover:text-white" data-testid="settings-btn">
              <Settings className="w-5 h-5" />
            </Button>
          </Link>
          
          <Button variant="ghost" size="icon" className="text-text-muted hover:text-white" onClick={logout} data-testid="logout-btn">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default AuthHeader;
